import React, { useState, useEffect, useContext } from "react";
import {
  getRecipe,
  likeRecipe,
  dislikeRecipe,
  addFavourite,
  removeFavourite,
} from "../api";
import { useParams, useNavigate } from "react-router-dom";
import { UserContext } from "../UserContext"; // Importer le contexte utilisateur
import {
  Button,
  Typography,
  Box,
  Grid,
  TextField,
  IconButton,
  Card,
  CardContent,
  MenuItem,
} from "@mui/material";
import { Facebook, Twitter, WhatsApp, Share } from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const API_BASE_URL = "https://pwa-backend.fenrir-tech.fr"; // Replace with your API URL

const RecipeDetailsPage = () => {
  const { id } = useParams();
  const { user } = useContext(UserContext); // Récupérer l'utilisateur connecté
  const [recipe, setRecipe] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [newRating, setNewRating] = useState(null);
  const [liked, setLiked] = useState(false); // État pour savoir si l'utilisateur a liké
  const [disliked, setDisliked] = useState(false); // État pour savoir si l'utilisateur a disliké
  const [isFavourite, setIsFavourite] = useState(false); // État pour savoir si la recette est en favoris
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchRecipeAndComments = async () => {
      try {
        const data = await getRecipe(id);
        setRecipe(data);

        const commentsData = await fetch(
          `${API_BASE_URL}/recipts/${id}/comments`
        );
        if (commentsData.ok) {
          setComments(await commentsData.json());
        }

        if (user) {
          // Vérifiez si l'utilisateur a liké cette recette
          const response = await fetch(
            `${API_BASE_URL}/recipts/${id}/like-status`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          );
          if (response.ok) {
            const { liked } = await response.json();
            setLiked(liked);
          }

          // Vérifiez si la recette est en favoris
          const favouriteResponse = await fetch(
            `${API_BASE_URL}/recipts/favourites`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          );
          if (favouriteResponse.ok) {
            const favourites = await favouriteResponse.json();
            setIsFavourite(favourites.some((fav) => fav.id === parseInt(id)));
          }
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchRecipeAndComments();
  }, [id, user]);

  const handleLike = async () => {
    try {
      const data = await likeRecipe(id);
      setRecipe({ ...recipe, likeCount: data.likeCount });
      setLiked(true);
      setDisliked(false); // Supprimez le dislike si existant
    } catch (err) {
      alert(`Erreur : ${err.message}`);
    }
  };

  const handleDislike = async () => {
    try {
      const data = await dislikeRecipe(id);
      setRecipe({ ...recipe, likeCount: data.likeCount });
      setLiked(false); // Supprimez le like si existant
      setDisliked(true);
    } catch (err) {
      alert(`Erreur : ${err.message}`);
    }
  };

  const handleAddFavourite = async () => {
    try {
      await addFavourite(id);
      setIsFavourite(true);
      alert("Recette ajoutée aux favoris !");
    } catch (err) {
      alert(`Erreur : ${err.message}`);
    }
  };

  const handleRemoveFavourite = async () => {
    try {
      await removeFavourite(id);
      setIsFavourite(false);
      alert("Recette retirée des favoris.");
    } catch (err) {
      alert(`Erreur : ${err.message}`);
    }
  };

  const handleAddComment = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/recipts/${id}/comments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ content: newComment, rating: newRating }),
      });

      if (!response.ok) {
        throw new Error("Failed to add comment");
      }

      const comment = await response.json();
      setComments([...comments, comment]); // Ajoutez le commentaire avec l'utilisateur inclus
      setNewComment("");
      setNewRating(null);
    } catch (err) {
      alert(`Erreur : ${err.message}`);
    }
  };

  const handleEditComment = (comment) => {
    setNewComment(comment.content); // Pré-remplir le champ avec le contenu existant
    setNewRating(comment.rating); // Pré-remplir la note existante
    setEditingCommentId(comment.id); // Stocker l'ID du commentaire en cours de modification
  };

  const saveEditedComment = async () => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/recipts/comments/${editingCommentId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({ content: newComment, rating: newRating }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update comment");
      }

      const updatedComment = await response.json();
      setComments(
        comments.map((comment) =>
          comment.id === updatedComment.id ? updatedComment : comment
        )
      );
      setNewComment("");
      setNewRating(null);
      setEditingCommentId(null);
    } catch (err) {
      alert(`Erreur : ${err.message}`);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/recipts/comments/${commentId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete comment");
      }

      setComments(comments.filter((comment) => comment.id !== commentId));
    } catch (err) {
      alert(`Erreur : ${err.message}`);
    }
  };

  const handleCopyLink = () => {
    const recipeUrl = `${window.location.origin}/recipes/${id}`;
    navigator.clipboard
      .writeText(recipeUrl)
      .then(() => alert("Lien copié dans le presse-papiers !"))
      .catch((err) => console.error("Erreur lors de la copie du lien :", err));
  };

  const shareLinks = recipe
    ? {
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
          `${window.location.origin}/recipes/${id}`
        )}`,
        twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(
          `Découvrez cette recette incroyable : ${recipe.name}`
        )}&url=${encodeURIComponent(
          `${window.location.origin}/recipes/${id}`
        )}`,
        whatsapp: `https://api.whatsapp.com/send?text=${encodeURIComponent(
          `Découvrez cette recette incroyable : ${recipe.name} - ${window.location.origin}/recipes/${id}`
        )}`,
      }
    : null;

  if (loading) {
    return <p>Chargement...</p>;
  }

  if (error) {
    return <p style={{ color: "red" }}>Erreur : {error}</p>;
  }

  if (!recipe) {
    return <p>Recette introuvable.</p>;
  }

  return (
    <div
      className="recipe-details-container"
      style={{ maxWidth: "900px", margin: "0 auto", padding: "20px" }}
    >
      <Box container sx={{ display: "flex", justifyContent: "space-around" }}>
        <div style={{ flex: 1 }}>
          <Button
            onClick={() => navigate(-1)}
            variant="outlined"
            color="secondary"
            sx={{ marginBottom: "20px" }}
            startIcon={<ArrowBackIcon />}
          >
            <span />
          </Button>
        </div>
        <div style={{ flex: 4 }}>
          <Typography variant="h4" gutterBottom>
            {recipe.name}
          </Typography>
        </div>
      </Box>

      {/* Recipe Image */}
      {recipe.image && (
        <Box sx={{ marginBottom: "20px" }}>
          <img
            src={recipe.image} // Utilise directement l'image encodée en base64
            alt={recipe.name}
            style={{ maxWidth: "100%", borderRadius: "8px" }}
          />
        </Box>
      )}

      <Grid
        container
        spacing={2}
        justifyContent="center"
        alignItems="center"
        sx={{ marginBottom: "10px" }}
      >
        <Grid item xs={12} md={6}>
          <Typography variant="body1">
            <strong>Nombre de personnes :</strong> {recipe.servings}
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="body1">
            <strong>Temps de préparation :</strong> {recipe.preparationTime}{" "}
            minutes
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="body1">
            <strong>Temps de cuisson :</strong> {recipe.cookingTime} minutes
          </Typography>
        </Grid>
      </Grid>

      {/* Recipe Description */}
      <Typography variant="body1" paragraph>
        <strong>Description :</strong> {recipe.description}
      </Typography>

      {/* Ingredients */}
      <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
        Ingrédients :
      </Typography>
      <ul>
        {recipe.ingredients.map((ingredient, index) => (
          <li key={index}>
            {ingredient.name} - {ingredient.quantity}
          </li>
        ))}
      </ul>

      {/* Recipe Instructions */}
      <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
        Instructions :
      </Typography>
      <ul>
        {recipe.receipt.map((step, index) => (
          <li key={index}>{step}</li>
        ))}
      </ul>

      {/* Likes and Dislikes */}
      <Typography variant="body1">
        <strong>Likes :</strong> {recipe.likeCount}
      </Typography>

      {/* Like, Dislike, and Favourite Buttons */}
      {user ? (
        <>
          <Box sx={{ marginBottom: "20px" }}>
            <Button
              onClick={handleLike}
              disabled={liked}
              variant="outlined"
              color="primary"
              sx={{ marginRight: "10px", width: "150px", height: "50px" }}
            >
              Like
            </Button>
            <Button
              onClick={handleDislike}
              disabled={disliked}
              variant="outlined"
              color="error"
              sx={{ marginRight: "10px", width: "150px", height: "50px" }}
            >
              Dislike
            </Button>
            <Button
              onClick={isFavourite ? handleRemoveFavourite : handleAddFavourite}
              variant="outlined"
              color={isFavourite ? "warning" : "default"}
              sx={{ width: "150px", height: "50px" }}
            >
              {isFavourite ? "Retirer des Favoris" : "Ajouter aux Favoris"}
            </Button>
          </Box>
        </>
      ) : (
        <Typography variant="body2" color="textSecondary" paragraph>
          Connectez-vous pour liker, disliker ou ajouter aux favoris cette
          recette.
        </Typography>
      )}

      {/* Back Button */}

      {/* Share Section */}
      <Box sx={{ marginBottom: "20px" }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
          Partager cette recette
        </Typography>
        <Box sx={{ display: "flex", gap: "5%", justifyContent: "center" }}>
          <IconButton
            color="primary"
            href={shareLinks.facebook}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Facebook />
          </IconButton>
          <IconButton
            color="primary"
            href={shareLinks.twitter}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Twitter />
          </IconButton>
          <IconButton
            color="primary"
            href={shareLinks.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
          >
            <WhatsApp />
          </IconButton>
        </Box>
        <Button
          onClick={handleCopyLink}
          variant="outlined"
          startIcon={<Share />}
          sx={{ marginTop: "10px" }}
        >
          Copier le lien
        </Button>
      </Box>

      {/* Comments Section */}
      <Typography variant="h6" gutterBottom>
        Commentaires et Notes
      </Typography>
      {comments.map((comment) => (
        <Card key={comment.id} sx={{ marginBottom: "15px" }}>
          <CardContent>
            <Typography variant="body1">
              <strong>
                {comment.User ? comment.User.name : "Utilisateur inconnu"} :
              </strong>{" "}
              {comment.content}
            </Typography>
            {comment.rating && (
              <Typography variant="body2">Note : {comment.rating}/5</Typography>
            )}
            {user && comment.User && comment.User.id === user.id && (
              <Box sx={{ marginTop: "10px" }}>
                <Button size="small" onClick={() => handleEditComment(comment)}>
                  Modifier
                </Button>
                <Button
                  size="small"
                  color="error"
                  onClick={() => handleDeleteComment(comment.id)}
                >
                  Supprimer
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      ))}

      {/* Add New Comment */}
      {user && (
        <Box sx={{ marginTop: "20px" }}>
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Ajouter un commentaire..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            sx={{ marginBottom: "10px" }}
          />
          <TextField
            select
            label="Note (optionnel)"
            value={newRating || ""}
            onChange={(e) => setNewRating(e.target.value)}
            sx={{ marginBottom: "10px", width: "100px" }}
          >
            {[1, 2, 3, 4, 5].map((rating) => (
              <MenuItem key={rating} value={rating}>
                {rating}
              </MenuItem>
            ))}
          </TextField>
          {editingCommentId ? (
            <Button
              onClick={saveEditedComment}
              variant="contained"
              color="primary"
            >
              Enregistrer
            </Button>
          ) : (
            <Button
              onClick={handleAddComment}
              variant="contained"
              color="primary"
            >
              Ajouter
            </Button>
          )}
        </Box>
      )}
    </div>
  );
};

export default RecipeDetailsPage;
